import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import DiscardNovaConversationMutation from '../../../../mutations/nova/DiscardNovaConversationMutation';
import DismissNovaRunMutation from '../../../../mutations/nova/DismissNovaRunMutation';
import RunNovaAgentProductionMutation from '../../../../mutations/nova/RunNovaAgentProductionMutation';
import type {DiscardNovaConversationMutation$data} from '../../../../mutations/nova/__generated__/DiscardNovaConversationMutation.graphql';
import type {DismissNovaRunMutation$data} from '../../../../mutations/nova/__generated__/DismissNovaRunMutation.graphql';
import type {RunNovaAgentProductionMutation$data} from '../../../../mutations/nova/__generated__/RunNovaAgentProductionMutation.graphql';
import Util from '../../util/util';
import type {NovaAgentSummary} from './novaAgentSummary';
import {creditsAvailableSince, creditsRequestMark, isNovaCreditsBlocked, noteNovaCreditsRefused} from './novaCredits';

// Empty, not MOCK_AGENTS: the picker already renders a stated empty state for an empty list
// (nova_invocation.picker.empty_state), so there is no need for a placeholder catalogue to cover
// the gap before the real fetch resolves. Seeding this with the fixture would mean a customer with
// `nova_agent_invocation` on sees nine invented agents ("General", "FAQ writer", ...) until the
// fetch lands — and indefinitely if it fails, or if useNovaAvailableAgents' fetch is ever lost to
// an unmount before it resolves. Tests that want the fixture's agents ask for them explicitly via
// setAvailableAgents(MOCK_AGENTS), the same as any other test fixture.
let availableAgents: NovaAgentSummary[] = [];

export function getAvailableAgents(): NovaAgentSummary[] {
	return availableAgents;
}

// Whether a bare Action click has anything to run. False in three distinct situations that are
// indistinguishable from here and want the same answer from the control: the fetch has not resolved
// yet, it resolved to an account with no granted agents, and it degraded to `[]` because the run
// service was unreachable (ViewerType.js resolves novaAgentsForInvocation with `.catch(() => [])`,
// so a blip costs the list rather than the whole viewer query).
//
// Deliberately not split into pending/empty. A separate "still loading" state would need a disabled
// reason of its own — new copy, a new member of the reason vocabulary — to say something the person
// cannot act on either way, and every one of the three means the same thing to them right now: this
// control has no agent to run. The first-load window it covers is one per session, not one per row.
export function hasAgentToRun(): boolean {
	return availableAgents.length > 0;
}

// The one write path once real data exists: a fetch resolving to the customer's actual agent
// list calls this once, and every picker instance — there is one store, read by all of them —
// sees the same list from then on. Dispatches so an already-open picker (or one that opened
// before a slow fetch resolved) updates live rather than only on its next mount.
export function setAvailableAgents(agents: NovaAgentSummary[]): void {
	availableAgents = agents;
	dispatch(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, undefined);
}

export function subscribeToAvailableAgents(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<void>(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, handler);
	return () => unsubscribe<void>(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, handler);
}

// The run service's own failure types, as the saved record's `failureState` reports them. Only the
// types whose customer-facing state this side has copy for are mapped here; completing the table
// against the full taxonomy, and the copy for each, belongs with the failure-presentation work.
// `generic` is what an unmapped type resolves to — a stated generic is honest, where reporting it
// as some other named cause would not be.
//
// The bar for an entry here is copy that already ships: a type left out of this table whose cause
// has a translated string and a CAUSE_MESSAGE_ID entry is not an incomplete table, it is shipped
// copy that nothing can reach. `document_processing` and `throttled` were both in that position.
export type FailureCause =
	| 'missing_access'
	| 'timeout'
	| 'unavailable_model'
	| 'permission'
	// The person's AI access was removed. Not a task permission, so not the permission copy: the
	// fix is an administrator restoring access, not anything on the task.
	| 'no_ai_access'
	| 'document_processing'
	| 'at_capacity'
	// The account's AI credits for the month are spent. A hard stop until the pool resets on the 1st,
	// not breakage - so it has copy of its own that says so and offers no retry.
	| 'out_of_credits'
	| 'generic';

const FAILURE_CAUSE_BY_RECORD_STATE: Record<string, FailureCause> = {
	missing_access: 'missing_access',
	// A definition the run service could not assemble — an absent agent record, or a record naming a
	// version it does not hold. It maps this onto the same customer-facing state as a source it could
	// not read, because from the caller's side both are "something this run needed was not there".
	missing_input: 'missing_access',
	timeout: 'timeout',
	model_unavailable: 'unavailable_model',
	model_retired: 'unavailable_model',
	// A source the run service could read but not parse. Its own cause, rather than folded into
	// missing_access: the remedy is the document, not the grant.
	document_processing: 'document_processing',
	// The model was busy and did not answer within the run deadline. Capacity, not breakage — and
	// at_capacity's copy, which promises that trying again shortly works, is exactly what this is.
	throttled: 'at_capacity',
	// The row a budget refusal writes before it refuses (NovaAgentLogic.runPreflightChecks). Reopening
	// the task reads it back, and it must say what the refusal said: the credits are used up.
	budget_exhausted: 'out_of_credits',
};

// Refusals the platform raises before a run starts that have no copy of their own yet. They are
// known names, so warning about them as unmapped gaps would be wrong - they resolve to generic.
const KNOWN_REFUSAL_STATES = new Set(['service_paused', 'run_ceiling']);

// An unrecognised failure type is a mapping gap rather than a bug in the run: the run really did
// fail, and saying so generically is the truthful report. Warned rather than swallowed so the gap
// is visible in the console of whoever hits it first.
export function failureCauseFromRecord(failureState?: string | null): FailureCause {
	if (!failureState) return 'generic';
	if (KNOWN_REFUSAL_STATES.has(failureState)) return 'generic';
	const mapped = FAILURE_CAUSE_BY_RECORD_STATE[failureState];
	if (!mapped) {
		// eslint-disable-next-line no-console
		console.warn(`nova run failed with an unmapped failure type: ${failureState}`);
		return 'generic';
	}
	return mapped;
}

// The refusal codes NovaAgentLogic sets on the GraphQLError it throws before a run starts. Only
// RUN_CEILING is a "not now": BUDGET_EXHAUSTED and SERVICE_PAUSED are refusals that trying again
// shortly does not clear, so mapping them onto at_capacity's copy would promise a retry that cannot
// work. BUDGET_EXHAUSTED has copy of its own that says when the credits come back; SERVICE_PAUSED
// stays generic until it has some.
//
// NO_EDIT_TASK is the permission refusal. It reaches this side as a code and nothing else, because
// the server masks the message, so without an entry here it resolved to generic — "something went
// wrong" in place of the one reason the person could act on. It is reachable even though every
// control that starts a run is already disabled for this person: a permission revoked while the tab
// is open, or a client and server that disagree, both land here.
const FAILURE_CAUSE_BY_REFUSAL_CODE: Record<string, FailureCause> = {
	RUN_CEILING: 'at_capacity',
	NO_EDIT_TASK: 'permission',
	AI_ACCESS_DENIED: 'no_ai_access',
	BUDGET_EXHAUSTED: 'out_of_credits',
};

// The GraphQL errors a Relay mutation failure carries, if it carries any. `createRequestError`
// (react-relay-network-modern) formats only each error's `message` and locations into the Error's
// own message — `extensions` never reaches the string — but it also hangs the whole response off
// the error as `res`, and `res.errors` is the untouched errors array, extensions and all. That is
// the only place a refusal's code survives on this side.
//
// Read defensively rather than cast: this walks an error shape the type system knows nothing about,
// and a batching or middleware change upstream could stop producing it without a compile error.
function refusalCodesOf(error: unknown): string[] {
	const res = (error as {res?: unknown} | null)?.res;
	const errors = (res as {errors?: unknown} | null)?.errors;
	if (!Array.isArray(errors)) return [];
	return errors
		.map(entry => (entry as {extensions?: {code?: unknown}} | null)?.extensions?.code)
		.filter((code): code is string => typeof code === 'string');
}

// A refusal the platform raised before the run started arrives as an error rather than a saved
// record, so it carries no failure type to map — only the code it was thrown with. A capacity
// refusal is the one that must not read as "something went wrong": it is a "not now", and the
// platform's own copy already says trying again shortly will work.
//
// Keyed on the code rather than on the message this previously matched: the code is a contract
// between this surface and NovaAgentLogic, where the message is customer-facing copy that the
// translations workflow can change underneath a regex without anything failing.
// Whether a refusal says it wrote the person's reply down before refusing it. A caller holding that
// text needs to know: the message is already on a turn the conversation renders, so handing it back
// to the box it came from would leave the same message in two places and invite sending it twice.
//
// Read off the same error shape as the code above, and defensively for the same reason: absence is
// "it was not recorded", which is the answer that keeps the text rather than drops it.
export function refusalRecordedTheReply(error: unknown): boolean {
	const res = (error as {res?: unknown} | null)?.res;
	const errors = (res as {errors?: unknown} | null)?.errors;
	if (!Array.isArray(errors)) return false;
	return errors.some(entry => (entry as {extensions?: {replyRecorded?: unknown}} | null)?.extensions?.replyRecorded === true);
}

export function failureCauseFromRefusal(error: unknown): FailureCause {
	for (const code of refusalCodesOf(error)) {
		const mapped = FAILURE_CAUSE_BY_REFUSAL_CODE[code];
		if (mapped) return mapped;
	}
	return 'generic';
}

export type RunOutcome =
	| {kind: 'succeeded'}
	// `replyRecorded` is for the one failure that saved the person's words without producing a turn
	// to carry them: a refusal raised before the run started. Everywhere else the turn itself is the
	// answer to "did my message land", and this stays absent.
	| {kind: 'failed'; cause: FailureCause; replyRecorded?: boolean}
	| {kind: 'cancelled'};

// One saved run, in the shape a conversation turn needs to render itself without a refetch — see
// NovaConversationTurnProps['run'] (NovaConversationTurn.tsx), which this deliberately mirrors
// field-for-field. Kept as its own type here rather than imported from there: that component sits
// above this store (it reads getAvailableAgents from it), so importing it back would be circular.
export interface NovaThreadTurn {
	id: string;
	// The conversation this turn belongs to. Carried so a reader can tell a reply on the thread it is
	// showing from a run that started a new one - changing the agent does exactly that.
	threadId: string | null;
	agentId: string | null;
	status: string | null;
	outcome: string | null;
	answer: string | null;
	userMessageText: string | null;
	// Who asked. Carried on the turn rather than read from the viewer, so a thread several people
	// have replied on names each of them against their own turn.
	actingPerson?: {fullName: string | null; profilePictureId: string | null} | null;
	ranAt: string | null;
	failureState: string | null;
	grounding: {resolved?: ReadonlyArray<{token: string | null; scope: string | null}> | null} | null;
}

// A run's outcome, plus the turn it produced — a caller rendering a conversation needs both: the
// outcome to drive this control's own state, the turn to append to what it already shows. Every run
// that saved a record carries one, opening run or reply alike; `turn` is absent only where nothing
// was saved, which is a refusal raised before the run started, a failure, or a stop.
export type RunResult = RunOutcome & {turn?: NovaThreadTurn};

// Refusals invoke() itself raises rather than the run service — the three-run ceiling, no agent to
// run, a run already in flight for this task, or a result that arrived after something superseded
// it. Distinct from a `RunResult`'s 'failed' kind: the run never started, so there is nothing to
// report as having failed.
export type InvokeRefusal = {
	kind: 'refused';
	// 'stopped' and 'superseded' are both a run that lost its place, told apart by who took it. Every
	// displacement reachable today begins with the person pressing Stop - invoke refuses while a run
	// is in flight, so nothing else can take a running run's place - and 'stopped' is what says so.
	// 'superseded' remains the honest label for a displacement that arrives some other way; blaming
	// "something else" for what the person just did themselves is what this tells apart.
	reason: 'already_running' | 'at_run_cap' | 'no_agent' | 'superseded' | 'stopped' | 'out_of_credits';
};

// What invoke() resolves to: either the run's own result (succeeded/failed/cancelled, with a turn
// when one was produced) or a refusal that means no run ran at all. A reply caller reads this to
// know whether its message actually went anywhere — the three existing call sites (my-work-row,
// table-row, card) don't read the resolved value at all, so widening it here changes nothing for
// them; they only ever cared about the state change invoke() dispatches, never its return value.
export type InvokeResult = RunResult | InvokeRefusal;

export interface RunState {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	// RunResult rather than RunOutcome: a reply's outcome carries the turn it produced, and this is
	// the state a caller re-rendering from subscribeToRunState (rather than reading invoke()'s own
	// resolved value) reads it from.
	outcome: RunResult | null;
	// The most recent turn this session produced for the task, kept past the outcome that carried it.
	//
	// `outcome` is replaced wholesale by every run, and several of them carry no turn at all - a
	// refusal, a failure, a stop. Readers that need to know which conversation this session is on
	// (the reply composer, which must continue the thread the last run actually created) would
	// otherwise lose it the moment a run failed, and fall back to whatever the page was loaded with.
	lastTurn: NovaThreadTurn | null;
	// What the person typed into the reply box, kept only while their run is in flight.
	//
	// The thread shows a reply the moment it is sent rather than when its answer lands, so something
	// has to hold the text across that gap. It lives here because the thread learns everything else
	// about a run from this store, and because the message is already passed through invoke() — the
	// alternative was a second channel carrying the same string to the same component.
	//
	// Null for an opening run, which has no typed message, and null again once the run resolves: by
	// then the saved turn carries the text and a second copy would render twice.
	pendingMessage: string | null;
}

// Once three tasks are running at once, every other idle control disables itself with a reason
// until one finishes. This is a client-simulated ceiling over this same store, not a real
// account-wide limit (that depends on a platform-side capacity check that doesn't exist yet) —
// it mirrors the mockup's own local running-count check.
export const RUN_CAP = 3;

const runStates = new Map<string, RunState>();

// Tracks how many tasks are currently running, kept in step with the two places below that flip
// isRunning, rather than scanning the whole map on every read — the map only grows across a
// session and this count is read on every mounted control's mount and on every state change.
let runningCount = 0;

// A run is identified by its generation, not by its task: a task can have a stopped run still in
// flight while a new one starts, and "is this task running" cannot tell those two apart. Bumped by
// both invoke() and stop(), so a superseded run's late result recognises that it is no longer the
// run this task is waiting on and does nothing.
const runGeneration = new Map<string, number>();

function nextGeneration(taskId: string): number {
	const next = (runGeneration.get(taskId) ?? 0) + 1;
	runGeneration.set(taskId, next);
	return next;
}

// The generation each task was last stopped at, so a run that finds itself displaced can say whether
// the person stopped it or another run took its place. Keyed by the generation being retired, which
// is exactly what the displaced run is still holding.
const stoppedGeneration = new Map<string, number>();

function readState(taskId: string): RunState {
	return (
		runStates.get(taskId) ?? {
			hasEverRun: false,
			isRunning: false,
			outputRead: false,
			outcome: null,
			lastTurn: null,
			pendingMessage: null,
		}
	);
}

// `lastTurn` is optional on the way in because this function is what maintains it: callers build the
// next state around the outcome they just got and have no reason to restate a turn from an earlier
// run.
function writeState(taskId: string, next: Omit<RunState, 'lastTurn'> & {lastTurn?: NovaThreadTurn | null}): void {
	// Carried forward rather than taken from `next` alone: callers build the next state around the
	// outcome they just got, and an outcome with no turn must not erase the turn an earlier run
	// produced. Only a newer turn replaces it, and clearRunState drops it with everything else.
	const carried = next.outcome?.turn ?? next.lastTurn ?? runStates.get(taskId)?.lastTurn ?? null;
	runStates.set(taskId, {...next, lastTurn: carried});
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

// Drops everything this session remembers about the task's runs, so every control reads it as a
// task that has never run. Deliberately not a writeState of a zeroed state: an entry that exists
// and says "never run" and no entry at all are the same thing to readState, and removing it keeps
// the map from growing an entry per discarded task. Still dispatches, since the controls that
// already read the old state have to hear that it is gone.
//
// Refuses to act on a task with a run in flight. runningCount is kept in step by whoever flipped
// isRunning, and forgetting a running task here would leave that decrement to a result that can no
// longer find its state - permanently consuming one of the three run slots.
function forgetRunState(taskId: string): void {
	if (readState(taskId).isRunning) return;
	runStates.delete(taskId);
	runGeneration.delete(taskId);
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

export function getRunState(taskId: string): RunState {
	return readState(taskId);
}

export function getRunningCount(): number {
	return runningCount;
}

export function isAtRunCap(): boolean {
	return runningCount >= RUN_CAP;
}

// Stands in for the platform's real account-wide budget/capacity check, which doesn't exist yet.
// Derives the same near/at-limit shape from this local run-cap simulation — swap the body, not
// the callers, once a real account-level check exists.
export function getAccountLimitNotice(): 'near_limit' | 'at_limit' | null {
	if (runningCount >= RUN_CAP) return 'at_limit';
	if (runningCount === RUN_CAP - 1) return 'near_limit';
	return null;
}

// The one out-of-credits notice a task shows after an attempt was refused for a spent allowance,
// and the unanswered message that attempt carried. Held only in this tab and never sent anywhere:
// the refusal is a state of the account, not part of the conversation. A newer refusal replaces
// the older one, so a task never shows two.
export interface CreditsRefusal {
	message: string | null;
	author: {fullName: string | null; profilePictureId: string | null} | null;
	at: string;
}

// Each notice keeps the balance-request mark from when it was refused. Once a balance asked for
// after that shows credits to spend, the notice belongs to an earlier period and is dropped, whether
// or not its task's thread was open to see the credits come back.
const creditsRefusals = new Map<string, {refusal: CreditsRefusal; mark: number}>();

function dropStaleCreditsRefusals(): void {
	creditsRefusals.forEach((entry, taskId) => {
		if (creditsAvailableSince(entry.mark)) creditsRefusals.delete(taskId);
	});
}

function recordCreditsRefusal(taskId: string, message: string | null, author: CreditsRefusal['author']): void {
	dropStaleCreditsRefusals();
	creditsRefusals.set(taskId, {refusal: {message, author, at: new Date().toISOString()}, mark: creditsRequestMark()});
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

export function getCreditsRefusal(taskId: string): CreditsRefusal | null {
	const entry = creditsRefusals.get(taskId);
	if (!entry) return null;
	if (creditsAvailableSince(entry.mark)) {
		creditsRefusals.delete(taskId);
		return null;
	}
	return entry.refusal;
}

export function clearCreditsRefusal(taskId: string): void {
	if (!creditsRefusals.delete(taskId)) return;
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

// What `invoke()` needs back from a run, whoever performed it. Tests supply their own runner
// instead of a real commit, which is why this is the seam rather than the mutation module itself.
//
// `message`/`threadId` are optional and both-or-neither in practice: a reply on an existing
// conversation carries both (the thread to reply on and the question that reply asks), an opening
// run carries neither and lets the server mint a fresh thread. Nothing here enforces that pairing —
// it is the caller's contract to keep, the same way agentId's presence already is.
export type RunAgent = (args: {taskId: string; agentId: string; message?: string; threadId?: string}) => Promise<RunResult>;

// `graphql-relay` is a server-side dependency and is not available here, so the global id is
// encoded the same way the rest of web-front does it — plain base64 of "Type:rawId", which is what
// the platform's own `fromGlobalId` decodes. Not the ticketing tree's base64url variant: that
// strips padding for a different gateway's wire shape.
const novaAgentGlobalId = (agentId: string): string => btoa(`NovaAgent:${agentId}`);

// `taskId` arrives already a Relay global id — every mounting surface passes `task.id` from its own
// Relay data. The agent id does not: the picker's list comes from `viewer.novaAgentsForInvocation`,
// whose `agentId` is the agent's own string id, so it is encoded here to the global id the mutation
// decodes. Getting this backwards fails three services away, at the run service's own id check.
const commitRun: RunAgent = ({taskId, agentId, message, threadId}) =>
	new Promise(resolve => {
		Util.CommitMutation(
			RunNovaAgentProductionMutation,
			{id: novaAgentGlobalId(agentId), taskId, message, threadId},
			(response: RunNovaAgentProductionMutation$data) => {
				const run = response?.runNovaAgentProduction?.result?.run;
				// The saved record, reshaped into the turn a conversation renders — present whenever a
				// record came back at all, terminal outcome or not, so a still-running or oddly-shaped
				// result is still something the caller can show rather than nothing.
				const turn: NovaThreadTurn | undefined = run
					? {
							id: run.id,
							threadId: run.threadId,
							agentId: run.agentId,
							status: run.status,
							outcome: run.outcome,
							answer: run.answer,
							userMessageText: run.userMessageText,
							actingPerson: run.actingPerson ?? null,
							ranAt: run.ranAt,
							failureState: run.failureState,
							grounding: run.grounding,
					  }
					: undefined;
				if (run?.outcome === 'SUCCEEDED') {
					resolve({kind: 'succeeded', turn});
					return;
				}
				if (run?.outcome === 'FAILED') {
					resolve({kind: 'failed', cause: failureCauseFromRecord(run.failureState), turn});
					return;
				}
				// Neither terminal outcome came back. The run is not reported as succeeded on a shape
				// nothing confirmed, but nor is a named cause invented for it — generic is what the
				// record actually supports, and the warning is what makes the unread shape findable.
				// eslint-disable-next-line no-console
				console.warn(`nova run returned no terminal outcome (status ${run?.status ?? 'unknown'})`);
				resolve({kind: 'failed', cause: 'generic', turn});
			},
			true,
			null,
			// A refusal the platform raised before the run started — at the run ceiling, over budget,
			// the feature not enabled — arrives here rather than as a saved FAILED record. It is still
			// a run that produced nothing, so it resolves as a failure rather than leaving the control
			// stuck on Working. Passing an onError at all is what stops Util.CommitMutation opening its
			// generic error modal over a state this control already states on itself.
			//
			// That it arrives here at all is worth stating, because the intuitive guess is the opposite
			// and it would make this branch dead code: a refusal is thrown *after* the resolver has
			// produced a `data` payload, so one might expect Relay to treat it as a partial success and
			// call onCompleted. It does not. `fetchWithMiddleware` (react-relay-network-modern) throws
			// `createRequestError` whenever `res.errors` is present, whether or not `res.data` is —
			// so a RUN_CEILING refusal genuinely reaches this handler and never onCompleted.
			//
			// The refusal is also logged rather than only mapped: the cause below narrows it to what the
			// surface can say, where the platform's own error carries its code and correlation id, which
			// is the only thing that identifies which refusal it actually was.
			(error: unknown) => {
				// eslint-disable-next-line no-console
				console.warn('nova run was refused before it started:', error);
				resolve({
					kind: 'failed',
					cause: failureCauseFromRefusal(error),
					replyRecorded: refusalRecordedTheReply(error),
				});
			}
		);
	});

// Retires the conversation the given run belongs to. The run rows themselves stay: a discard is
// the customer saying they do not want the draft, not a request to erase what was run — so what
// the surface can report afterwards is only whether the write landed.
//
// Resolves false rather than rejecting when it does not: every caller's next move is the same
// either way (leave the thread on screen and say nothing was discarded), and a rejection would
// force each of them to write a catch to reach that. An onError is passed for the same reason
// commitRun passes one — it is what stops Util.CommitMutation opening its generic error modal
// over a state the caller states on itself.
//
// `taskId` is what the discarded conversation belonged to. A successful discard forgets this
// session's run state for it, because that state is the only reason a row or card control would
// still be offering to read an answer that no longer exists — a task whose conversation is gone
// has never run as far as every surface is concerned, and must read as plain Action again.
export function discardConversation(runId: string, taskId: string): Promise<boolean> {
	return new Promise(resolve => {
		Util.CommitMutation(
			DiscardNovaConversationMutation,
			{id: runId},
			(response: DiscardNovaConversationMutation$data) => {
				// Read from the record's own answer rather than from the absence of an error: a discard
				// that matched no row - the run is still in flight, so it is not the customer's to
				// retire yet - answers 200 with the conversation untouched.
				const discarded = response?.discardNovaConversation?.run?.discarded === true;
				if (discarded) forgetRunState(taskId);
				resolve(discarded);
			},
			true,
			null,
			// The standard error flow still runs: onFailure is what raises the correlation-ID modal and
			// recognises an expired session, and swallowing it left a network failure looking exactly
			// like a discard the server declined. This override exists only to settle the promise the
			// caller is waiting on, so the confirmation can close and say something.
			(error: unknown, onFailure: (error: unknown) => boolean) => {
				const retrying = onFailure(error);
				// A transient failure schedules its own retry, and that attempt resolves this promise
				// through the success path - answering here would tell the caller it failed while it is
				// still in flight.
				if (!retrying) resolve(false);
				return retrying;
			}
		);
	});
}

// What a dismiss answers with. Two facts, because the caller needs both and can derive neither:
// whether this call actually hid a run, and whether the task has any conversation left afterwards.
//
// The second is the server's own answer rather than something the caller works out from the list it
// rendered. A run can land while the dismiss is in flight, and a caller counting what it had on
// screen would then report the conversation gone while a live turn is sitting in it.
export interface DismissResult {
	dismissed: boolean;
	// Null when nothing was learned - a refused request, or a payload carrying no task. Distinct from
	// false, which is the server saying the task has nothing left.
	taskHasConversation: boolean | null;
}

// Clears one failed run from the thread it sits on, leaving every other turn where it is.
//
// The same promise shape as discardConversation above, and resolving false rather than rejecting
// for the same reason: the caller's next move is identical either way - leave the notice on screen -
// and a rejection would make each of them write a catch to reach it.
//
// `taskId` is the task the dismissed run belonged to. This session's run state for it is forgotten
// only when the dismiss left the task with no conversation at all, which the payload's own
// hasNovaConversation is what says. Forgetting it unconditionally would reset the control on a task
// whose earlier answers are still on the thread, so a task with Nova output would offer plain Action.
export function dismissRun(runId: string, taskId: string): Promise<DismissResult> {
	return new Promise(resolve => {
		Util.CommitMutation(
			DismissNovaRunMutation,
			{id: runId},
			(response: DismissNovaRunMutation$data) => {
				// Read from the record's own answer rather than from the absence of an error: a dismiss
				// the guard refused - the run succeeded, or has not finished, so it is not an error
				// message to clear - answers 200 with the thread untouched.
				const dismissed = response?.dismissNovaRun?.run?.dismissed === true;
				// The server's own reading of what is left, which is the only one that is not a guess.
				// A caller deciding from the list it rendered cannot see a run that landed while this
				// request was in flight, and would report a conversation gone while a live turn is on
				// screen. Null when the payload carries no task - nothing was learned, rather than
				// "there is nothing left".
				const taskHasConversation = response?.dismissNovaRun?.task?.hasNovaConversation ?? null;
				if (dismissed && taskHasConversation === false) forgetRunState(taskId);
				resolve({dismissed, taskHasConversation});
			},
			true,
			null,
			// Same override, same reason as the discard above: the standard error flow still raises the
			// correlation-ID modal and recognises an expired session, and this only settles the promise
			// the caller is waiting on so it can say something rather than hang.
			(error: unknown, onFailure: (error: unknown) => boolean) => {
				const retrying = onFailure(error);
				if (!retrying) resolve({dismissed: false, taskHasConversation: null});
				return retrying;
			}
		);
	});
}

// Marks a turn this module invented to stand in for a record the server has, but that nothing has
// read back yet. The id after it is not a NovaAgentRun global id and the server would not know it.
const STAND_IN_TURN_PREFIX = 'refused-';

// Whether this turn's id is one this module invented rather than one a record actually carries.
//
// Any surface offering an action that addresses a run by id has to ask: sending a stand-in id to a
// mutation reaches the server as a run that does not exist, which answers NotFound and surfaces as
// a generic error the person cannot clear. The turn is real and worth showing - the refusal it
// stands for was saved - but it cannot be acted on until the thread is read back and the row's own
// id replaces this one.
export function isStandInTurnId(id: string): boolean {
	return id.startsWith(STAND_IN_TURN_PREFIX);
}

// A turn standing in for the record a refusal wrote on its way out.
//
// A refusal leaves as an error, so there is no run for the thread to read — but the server did save
// the person's words on a FAILED row, and reopening the task shows that row with the message above
// it. Without this the live thread shows neither: the composer has emptied its box on the strength
// of the message being saved, and nothing on screen is holding it until the modal is reopened.
//
// `failureState` is left null: every refusal that still records a reply saves a state that resolves
// to the generic cause (see KNOWN_REFUSAL_STATES), so null renders exactly as the saved row will. A
// budget refusal never gets here, because it records nothing.

function turnForRecordedRefusal(args: {
	taskId: string;
	agentId: string;
	threadId?: string;
	message: string;
	replyAuthor?: {fullName: string | null; profilePictureId: string | null} | null;
}): NovaThreadTurn {
	return {
		// Replaced by the saved row's own id the moment the thread is refetched. Marked as standing in
		// for a record rather than being one, so nothing mistakes it for an id the server would know -
		// see isStandInTurnId, which is how a caller asks.
		id: `${STAND_IN_TURN_PREFIX}${args.taskId}-${Date.now()}`,
		threadId: args.threadId ?? null,
		agentId: args.agentId,
		status: 'COMPLETE',
		outcome: 'FAILED',
		answer: null,
		userMessageText: args.message,
		// Named from the sender rather than left empty: this turn stands in for a record nothing has
		// read back yet, and a turn naming nobody reads as one whose person could not be found - so
		// the person's own words would come back to them described as someone else's.
		actingPerson: args.replyAuthor ?? null,
		ranAt: new Date().toISOString(),
		failureState: null,
		grounding: null,
	};
}

export function invoke(
	taskId: string,
	agentId?: string,
	opts?: {
		runAgent?: RunAgent;
		taskName?: string;
		companyProjectId?: string | number;
		companyTaskId?: string | number;
		projectColor?: string;
		// Both present together makes this a reply on an existing conversation; both absent makes it
		// an opening run that lets the server mint a fresh thread. See RunAgent's own note on why
		// nothing here enforces the pairing.
		message?: string;
		threadId?: string;
		// Who is sending this reply. Read only when a refusal records the message without handing back
		// a run - the turn standing in for that record has no other way to name its author, and a turn
		// naming nobody reads as one whose person could not be found.
		replyAuthor?: {fullName: string | null; profilePictureId: string | null} | null;
	}
): Promise<InvokeResult> {
	const current = readState(taskId);
	// A second call for a task already mid-run would otherwise run it twice and let the later
	// result overwrite the earlier one, which could silently revert a read output back to unread.
	// Observable rather than a bare resolved void: a reply caller needs to know its message never
	// went anywhere, distinctly from a reply that was sent and is merely still running. The three
	// existing call sites never read invoke()'s resolved value, so this is invisible to them.
	if (current.isRunning) return Promise.resolve({kind: 'refused', reason: 'already_running'});
	// Refused here as well as on the button, because the agent picker calls invoke() directly.
	if (isNovaCreditsBlocked()) {
		recordCreditsRefusal(taskId, opts?.message ?? null, opts?.replyAuthor ?? null);
		return Promise.resolve({kind: 'refused', reason: 'out_of_credits'});
	}
	// The cap is also enforced presentationally in deriveControlState, but that is the UI's
	// rendering of the invariant rather than the invariant itself — NovaAgentPickerForTask calls
	// invoke() directly, bypassing the disabled-button gate entirely.
	if (isAtRunCap()) return Promise.resolve({kind: 'refused', reason: 'at_run_cap'});
	// A run needs an agent. deriveControlState disables the control whenever the resolved list is
	// empty (see its no_agent_available branch), so reaching this from the control itself is not an
	// expected state — but NovaAgentPickerForTask calls invoke() directly, and nothing stops a
	// future caller doing the same, so the guard stays. Warned rather than returned silently: a
	// click that produces no state change, no notification and no log is indistinguishable from a
	// broken control, which is exactly how this read before.
	if (!agentId) {
		// eslint-disable-next-line no-console
		console.warn(`nova run was requested for task ${taskId} with no agent to run`);
		return Promise.resolve({kind: 'refused', reason: 'no_agent'});
	}
	runningCount += 1;
	const generation = nextGeneration(taskId);
	writeState(taskId, {...current, isRunning: true, outcome: null, pendingMessage: opts?.message ?? null});

	const runAgent = opts?.runAgent ?? commitRun;
	return runAgent({taskId, agentId, message: opts?.message, threadId: opts?.threadId})
		.catch((): RunResult => {
			// A runner that threw rather than reporting an outcome. The run still produced nothing, and
			// the state machine must leave Working either way — stranding the control there is worse
			// than reporting a generic failure.
			// eslint-disable-next-line no-console
			console.warn('nova run threw instead of reporting an outcome');
			return {kind: 'failed', cause: 'generic'};
		})
		.then((result): InvokeResult => {
			// Superseded while this was in flight — stopped, or stopped and re-invoked. Either way this
			// is no longer the run the task is waiting on: applying its result would double-decrement
			// the cap count and write an answer nothing asked for over whatever replaced it. Reported
			// as its own refusal reason rather than silently dropped, for the same reason the guards
			// above are: a reply caller that gets nothing back can't tell "sent, still running"
			// (elsewhere) from "the store dropped it".
			if (runGeneration.get(taskId) !== generation) {
				const stoppedByThePerson = stoppedGeneration.get(taskId) === generation;
				return {kind: 'refused', reason: stoppedByThePerson ? 'stopped' : 'superseded'};
			}
			runningCount -= 1;
			// The server refused for a spent allowance and saved nothing. A saved out-of-credits record
			// carries a turn, and is history rather than a fresh refusal.
			if (result.kind === 'failed' && result.cause === 'out_of_credits' && !result.turn) {
				noteNovaCreditsRefused();
				recordCreditsRefusal(taskId, opts?.message ?? null, opts?.replyAuthor ?? null);
			}
			// A failure the person can actually open: either the run saved a turn, or a refusal wrote
			// the reply down and said so. Both mean the conversation has something on it that was not
			// there before; every other failure leaves the task exactly as it was.
			const leftSomethingToRead = result.kind === 'failed' && (!!result.turn || !!result.replyRecorded);
			// A refusal that recorded the reply carries no turn of its own, and the composer has already
			// let go of the text on the strength of it being saved. Standing one in here is what keeps
			// the message on screen until the thread is next refetched.
			const outcome: InvokeResult =
				result.kind === 'failed' && result.replyRecorded && opts?.message
					? {
							...result,
							turn: turnForRecordedRefusal({
								taskId,
								agentId,
								threadId: opts.threadId,
								message: opts.message,
								replyAuthor: opts.replyAuthor,
							}),
					  }
					: result;
			writeState(taskId, {
				// Whether this run left the person something to open, which is the only thing Ready is
				// about. An answer qualifies; so does a failure the thread can show, because what was
				// asked and what Nova said went wrong are both on it and the task's own notification
				// sends the person here to look.
				//
				// Not every failure leaves that, and the kind alone cannot tell them apart - four
				// places in this file resolve `failed` and only two of them save anything. A platform
				// refusal before the run starts (commitRun's onError) and a runner that threw (the
				// catch above) both come back with no turn and nothing written, so there is nothing to
				// open: those return the control to Action, as they did before Ready covered failures
				// at all. `leftSomethingToRead` is that test, rather than the result's kind.
				//
				// A run the person stopped is excluded for a different reason: there may well be a
				// record, but they already know how it ended, because they ended it.
				hasEverRun: result.kind === 'succeeded' || leftSomethingToRead,
				isRunning: false,
				outputRead: false,
				outcome,
				pendingMessage: null,
			});
			return result;
		});
}

// A local cancel only: it returns this control to its pre-run state and frees the run-cap slot, but
// it cannot stop the run already in flight — whether the run service can cancel a run at all is an
// open question, and the real stop belongs with the failure-and-stop work. Bumping the generation is
// what supersedes the in-flight run, so its own resolution is ignored however long it takes and
// whatever the task is doing by then — including a newer run started in the meantime.
export function stop(taskId: string): void {
	const current = readState(taskId);
	if (!current.isRunning) return;
	// Recorded before the bump: the run still in flight is holding the generation being retired here,
	// and that is what lets it report "you stopped this" rather than "something else replaced it".
	stoppedGeneration.set(taskId, runGeneration.get(taskId) ?? 0);
	nextGeneration(taskId);
	runningCount -= 1;
	writeState(taskId, {
		hasEverRun: current.hasEverRun,
		isRunning: false,
		outputRead: false,
		outcome: {kind: 'cancelled'},
		pendingMessage: null,
	});
}

export function markRead(taskId: string): void {
	const current = readState(taskId);
	if (!current.hasEverRun || current.isRunning) return;
	writeState(taskId, {...current, outputRead: true});
}

export function subscribeToRunState(taskId: string, callback: () => void): () => void {
	const handler = (changedTaskId: string): void => {
		if (changedTaskId === taskId) callback();
	};
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

// The run cap and the row-highlight-on-unread-Ready feature are both properties of the whole
// tracked set, not of one taskId — a plain per-task subscription can't tell an idle task that a
// caller renders elsewhere just now hit (or dropped below) the ceiling.
export function subscribeToAnyRunStateChange(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

export function isUnreadReady(taskId: string): boolean {
	const state = readState(taskId);
	// hasEverRun can be stale-true from an earlier run carried through a later re-invoke (see
	// invoke()'s spread of `current`). A stop on that later attempt means there is nothing new to
	// flag: the person ended it themselves and already knows how it went. A failure is not in that
	// position - nobody asked for it, and the thread has something to show about it.
	const outcomeBlocksReady = state.outcome?.kind === 'cancelled';
	return state.hasEverRun && !state.isRunning && !state.outputRead && !outcomeBlocksReady;
}
